import React from "react";

/* ---------------- Button ---------------- */
const BTN_VARIANTS = {
  primary: { background: "var(--lf-blue-600)", color: "#fff", border: "1px solid var(--lf-blue-600)" },
  secondary: { background: "var(--surface-card)", color: "var(--text-primary)", border: "1px solid var(--border-strong)" },
  accent: { background: "var(--lf-orange-500)", color: "#fff", border: "1px solid var(--lf-orange-500)" },
  ghost: { background: "transparent", color: "var(--text-secondary)", border: "1px solid transparent" },
  danger: { background: "var(--lf-red-500)", color: "#fff", border: "1px solid var(--lf-red-500)" },
};
const BTN_HOVER = {
  primary: "var(--lf-blue-700)", secondary: "var(--surface-inset)", accent: "var(--lf-orange-600)",
  ghost: "var(--surface-inset)", danger: "var(--lf-red-600)",
};
const BTN_SIZES = {
  sm: { padding: "0 14px", height: "34px", fontSize: "13px" },
  md: { padding: "0 18px", height: "40px", fontSize: "14px" },
  lg: { padding: "0 22px", height: "46px", fontSize: "15px" },
};

export function Button({ variant = "primary", size = "md", fullWidth = false, children, style, disabled, ...rest }) {
  const [hover, setHover] = React.useState(false);
  const v = BTN_VARIANTS[variant] || BTN_VARIANTS.primary;
  const s = BTN_SIZES[size] || BTN_SIZES.md;
  return (
    <button
      {...rest}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
        borderRadius: "var(--radius-md)", fontFamily: "var(--font-sans)", fontWeight: "var(--fw-semibold)",
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1,
        width: fullWidth ? "100%" : "auto", whiteSpace: "nowrap",
        transition: "background-color var(--dur-base) var(--ease-standard), transform var(--dur-fast) var(--ease-out), box-shadow var(--dur-base)",
        transform: hover && !disabled ? "translateY(-1px)" : "none",
        boxShadow: variant === "primary" && hover ? "var(--shadow-soft)" : "none",
        backgroundColor: hover && !disabled ? BTN_HOVER[variant] : v.background,
        color: v.color, border: v.border, ...s, ...style,
      }}
    >
      {children}
    </button>
  );
}

/* ---------------- Field wrapper (label) ---------------- */
export function Field({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      {label && <span className="lf-eyebrow">{label}</span>}
      {children}
    </label>
  );
}

/* ---------------- Input ---------------- */
export function Input({ size = "md", style, ...rest }) {
  const [focus, setFocus] = React.useState(false);
  const h = size === "sm" ? 34 : 40;
  return (
    <input
      {...rest}
      onFocus={(e) => { setFocus(true); rest.onFocus?.(e); }}
      onBlur={(e) => { setFocus(false); rest.onBlur?.(e); }}
      style={{
        height: h, width: "100%", minWidth: 0, padding: "0 12px",
        background: "var(--surface-card)", color: "var(--text-primary)",
        border: `1px solid ${focus ? "var(--lf-blue-600)" : "var(--border-default)"}`,
        borderRadius: "var(--radius-md)", outline: "none",
        boxShadow: focus ? "var(--shadow-focus)" : "none",
        font: `var(--fw-regular) 14px/1 var(--font-sans)`,
        transition: "border-color var(--dur-base), box-shadow var(--dur-base)", ...style,
      }}
    />
  );
}

/* ---------------- Textarea ---------------- */
export function Textarea({ style, ...rest }) {
  const [focus, setFocus] = React.useState(false);
  return (
    <textarea
      {...rest}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      style={{
        minHeight: 84, resize: "vertical", padding: "10px 12px", width: "100%",
        background: "var(--surface-card)", color: "var(--text-primary)",
        border: `1px solid ${focus ? "var(--lf-blue-600)" : "var(--border-default)"}`,
        borderRadius: "var(--radius-md)", outline: "none",
        boxShadow: focus ? "var(--shadow-focus)" : "none",
        font: "var(--fw-regular) 14px/1.5 var(--font-sans)",
        transition: "border-color var(--dur-base), box-shadow var(--dur-base)", ...style,
      }}
    />
  );
}

/* ---------------- Select ---------------- */
export function Select({ options = [], size = "md", value, onChange, placeholder, style, ...rest }) {
  const h = size === "sm" ? 34 : 40;
  return (
    <div style={{ position: "relative", minWidth: 0 }}>
      <select
        value={value ?? ""}
        onChange={onChange}
        {...rest}
        style={{
          height: h, width: "100%", padding: "0 34px 0 12px", appearance: "none",
          background: "var(--surface-card)", color: value ? "var(--text-primary)" : "var(--text-tertiary)",
          border: "1px solid var(--border-default)", borderRadius: "var(--radius-md)", outline: "none",
          font: "var(--fw-regular) 14px/1 var(--font-sans)", cursor: "pointer", ...style,
        }}
      >
        {placeholder && <option value="" disabled>{placeholder}</option>}
        {options.map((o) => {
          const val = typeof o === "string" ? o : o.value;
          const lbl = typeof o === "string" ? o : o.label;
          return <option key={val} value={val}>{lbl}</option>;
        })}
      </select>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="2"
        style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
        <path d="m6 9 6 6 6-6" />
      </svg>
    </div>
  );
}

/* ---------------- Checkbox ---------------- */
export function Checkbox({ label, checked, onChange, "data-testid": testId }) {
  return (
    <label
      data-testid={testId}
      style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", userSelect: "none" }}
    >
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange?.(e.target.checked)}
        style={{ position: "absolute", opacity: 0, width: 1, height: 1, margin: 0 }}
      />
      <span
        aria-hidden="true"
        style={{
          width: 17, height: 17, flexShrink: 0, borderRadius: "var(--radius-xs)",
          border: `1.5px solid ${checked ? "var(--lf-blue-600)" : "var(--border-strong)"}`,
          background: checked ? "var(--lf-blue-600)" : "var(--surface-card)",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all var(--dur-fast) var(--ease-out)",
        }}
      >
        {checked && (
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        )}
      </span>
      <span style={{ font: "var(--fw-regular) 13px/1.3 var(--font-sans)", color: "var(--text-secondary)" }}>
        {label}
      </span>
    </label>
  );
}

/* ---------------- Badge ---------------- */
const BADGE_TONES = {
  solid: { background: "var(--lf-blue-600)", color: "#fff" },
  accent: { background: "var(--lf-orange-500)", color: "#fff" },
  bulk: { background: "var(--lf-emerald-600)", color: "#fff" },
  neutral: { background: "var(--surface-inset)", color: "var(--text-secondary)" },
};
export function Badge({ tone = "neutral", children, style }) {
  const t = BADGE_TONES[tone] || BADGE_TONES.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "4px 9px",
      borderRadius: "var(--radius-pill)", font: "var(--fw-semibold) 10px/1 var(--font-sans)",
      letterSpacing: "0.04em", textTransform: "uppercase", ...t, ...style,
    }}>{children}</span>
  );
}
